/**
 * BackgroundLayer — paints the world floor + obstacles + decorative landmarks.
 *
 * Replaces the original flat-checker / single-colour-rect background. The new
 * layer is composed of:
 *   1) a tiled hex-grid floor texture (TileSprite, depth -1000)
 *   2) a soft radial vignette frame (Image, depth -800)
 *   3) decorative landmark sprites (signal pylons, debris piles, beacons,
 *      crater rings around the mid relay) placed at map-deterministic offsets
 *      so each map has subtle visual hints of "where the action lives"
 *   4) obstacle bodies — drawn as tiled bevelled-metal crates with a
 *      pulsing neon rim (so cover reads as cover, Helldivers-style)
 *   5) a thin world-border frame
 *
 * Pure presentation: Sim has no concept of landmarks. The layer reads the
 * MapDef only to place obstacles + cluster landmarks safely outside their
 * occupied rects + the relay capture radii.
 */

import type { MapDef } from '@signalclash/shared';
import Phaser from 'phaser';

const TILE_KEY = 'bg_tile';
const VIGNETTE_KEY = 'vignette';
const SCANLINE_KEY = 'scanlines';
const LANDMARK_KEY = 'landmarks';
const OBSTACLE_KEY = 'obstacle_tile';
const FX_KEY = 'fx';

// V1.7 ambient atmosphere — slow-drifting dust motes + glowing embers across
// the arena floor. Cheap eye-candy that makes the world feel "lived in" and
// gives the eye something to track even during lulls. Count tuned for ~60 fps
// on mid-range mobile (additive blend).
// V1.8 facelift — embers nearly tripled (12 → 32) and alpha doubled inside
// paintAmbient. The previous numbers were invisible at default zoom; the new
// values make the lit area feel like a "place" with atmosphere.
const AMBIENT_DUST_COUNT = 36;
const AMBIENT_EMBER_COUNT = 32;

const RELAY_SAFE_RADIUS = 160;
const CORE_SAFE_RADIUS = 240;
const SPAWN_SAFE_RADIUS = 80;
const LANDMARK_MIN_DIST = 110;
const BORDER_COLOR = 0x2a3a48;
const BORDER_GLOW = 0x4a6a82;

interface LandmarkPlacement {
  x: number;
  y: number;
  frame: string;
  scale: number;
  rotation: number;
  depth: number;
}

interface AmbientParticle {
  img: Phaser.GameObjects.Image;
  vx: number; vy: number;
  phaseMs: number;   // base phase for the sine-wave alpha pulse
  pulseHz: number;
  baseAlpha: number;
  worldW: number; worldH: number;
}

export class BackgroundLayer {
  private readonly objs: Phaser.GameObjects.GameObject[] = [];
  // Update-driven sprites (pulsing landmark beacons, scanline drift).
  private scanlines: Phaser.GameObjects.TileSprite | null = null;
  private beacons: Phaser.GameObjects.Image[] = [];
  private ambient: AmbientParticle[] = [];
  private lastUpdateMs: number | null = null;

  constructor(private readonly scene: Phaser.Scene, map: MapDef) {
    this.paintFloor(map);
    this.paintObstacles(map);
    this.paintLandmarks(map);
    this.paintBorder(map);
    this.paintVignetteAndScanlines(map);
    this.paintAmbient(map);
  }

  /** Cleanup hook — call from scene SHUTDOWN to drop every game object. */
  dispose(): void {
    for (const o of this.objs) o.destroy();
    this.objs.length = 0;
    this.beacons.length = 0;
    this.ambient.length = 0;
    this.scanlines = null;
  }

  /** Subtle scanline drift and beacon pulse — call from scene update(). */
  update(timeMs: number): void {
    if (this.scanlines !== null) {
      this.scanlines.tilePositionY = (timeMs * 0.012) % 4;
    }
    // V1.8.3 — pulse range widened slightly (0.30..0.45 → 0.45..0.70) so
    // beacons clearly shine and breathe, matching the signal-marker
    // redesign. Still narrow enough that they don't strobe.
    const pulse = 0.45 + 0.25 * (0.5 + 0.5 * Math.sin(timeMs / 480));
    for (const b of this.beacons) b.setAlpha(pulse);
    // Ambient dust + embers drift slowly across the map and wrap at edges so
    // the count stays constant (no spawn/despawn churn).
    if (this.ambient.length > 0) {
      const dtSec = this.lastUpdateMs === null ? 0 : Math.max(0, Math.min(0.1, (timeMs - this.lastUpdateMs) / 1000));
      this.lastUpdateMs = timeMs;
      for (const p of this.ambient) {
        let nx = p.img.x + p.vx * dtSec;
        let ny = p.img.y + p.vy * dtSec;
        // Wrap into the map bounds so particles loop forever cheaply.
        if (nx < -20) nx = p.worldW + 20;
        if (nx > p.worldW + 20) nx = -20;
        if (ny < -20) ny = p.worldH + 20;
        if (ny > p.worldH + 20) ny = -20;
        p.img.setPosition(nx, ny);
        // Slow sinusoidal twinkle.
        const a = p.baseAlpha * (0.55 + 0.45 * Math.sin((timeMs + p.phaseMs) / 1000 * p.pulseHz * Math.PI * 2));
        p.img.setAlpha(a);
      }
    }
  }

  // -------------------------------------------------------------------------
  // Floor — tiled hex-grid texture
  // -------------------------------------------------------------------------
  private paintFloor(map: MapDef): void {
    if (!this.scene.textures.exists(TILE_KEY)) {
      // Fallback: flat charcoal rectangle so the scene isn't empty in tests.
      const g = this.scene.add.graphics();
      g.setDepth(-1000);
      g.fillStyle(0x121519, 1).fillRect(0, 0, map.width, map.height);
      this.objs.push(g);
      return;
    }
    const tile = this.scene.add.tileSprite(0, 0, map.width, map.height, TILE_KEY);
    tile.setOrigin(0, 0);
    tile.setDepth(-1000);
    this.objs.push(tile);
    // A second very faint TileSprite shifted 90° gives the floor extra texture
    // depth (the eye reads it as two layers of plating rather than a single
    // tile). Cheap on the GPU — both are 256×256 textures.
    const tile2 = this.scene.add.tileSprite(0, 0, map.width, map.height, TILE_KEY);
    tile2.setOrigin(0, 0);
    tile2.setDepth(-999);
    tile2.setAngle(90);
    tile2.setAlpha(0.18);
    this.objs.push(tile2);
  }

  // -------------------------------------------------------------------------
  // Obstacles — bevelled metal crates + neon-rim accent
  // -------------------------------------------------------------------------
  private paintObstacles(map: MapDef): void {
    const useTexture = this.scene.textures.exists(OBSTACLE_KEY);
    for (const o of map.obstacles) {
      // Drop shadow.
      const sh = this.scene.add.graphics();
      sh.setDepth(-520);
      sh.fillStyle(0x000000, 0.45);
      sh.fillRect(o.x + 4, o.y + 6, o.w, o.h);
      this.objs.push(sh);

      if (useTexture) {
        const t = this.scene.add.tileSprite(o.x, o.y, o.w, o.h, OBSTACLE_KEY);
        t.setOrigin(0, 0);
        t.setDepth(-500);
        this.objs.push(t);
      } else {
        const g = this.scene.add.graphics();
        g.setDepth(-500);
        g.fillStyle(0x2a3340, 1).fillRect(o.x, o.y, o.w, o.h);
        this.objs.push(g);
      }
      // V1.8 facelift — bevel with a recessed inner panel edge. Crates are
      // background cover but still need visible weight to read as solid
      // objects. Three-pass treatment: outer outline + bright top-left
      // highlight + dark bottom-right shadow gives the raised-3D read; an
      // inset rebate ~4px inside the outer edge adds a recessed panel feel
      // (Helldivers-style armoured plate identity). No bright neon rim, no
      // hazard stripe — just metal.
      const rim = this.scene.add.graphics();
      rim.setDepth(-490);
      // Outer dark outline — anchors the crate's silhouette.
      rim.lineStyle(2, 0x05080c, 1);
      rim.strokeRect(o.x + 0.5, o.y + 0.5, o.w - 1, o.h - 1);
      // Bright lit edge on TOP + LEFT (light direction from top-left).
      // Alpha lifted from 0.10 → 0.22 so the bevel actually reads at
      // playable zoom.
      rim.lineStyle(1, 0xffffff, 0.22);
      rim.beginPath();
      rim.moveTo(o.x + 2, o.y + o.h - 1);
      rim.lineTo(o.x + 2, o.y + 1.5);
      rim.lineTo(o.x + o.w - 1, o.y + 1.5);
      rim.strokePath();
      // Deep shadow edge on BOTTOM + RIGHT (unlit sides).
      rim.lineStyle(1, 0x000000, 0.65);
      rim.beginPath();
      rim.moveTo(o.x + o.w - 1.5, o.y + 1.5);
      rim.lineTo(o.x + o.w - 1.5, o.y + o.h - 1.5);
      rim.lineTo(o.x + 1.5, o.y + o.h - 1.5);
      rim.strokePath();
      // Recessed inner panel rebate — a thin dark rectangle inset 4px from
      // the edges. Reads as "the centre of the plate is slightly lower than
      // the rim" → engineered cover, not a flat box. Skipped on small
      // obstacles where the inset would overlap the outer rim.
      if (o.w >= 36 && o.h >= 28) {
        rim.lineStyle(1, 0x000000, 0.40);
        rim.strokeRect(o.x + 4.5, o.y + 4.5, o.w - 9, o.h - 9);
        rim.lineStyle(1, 0xffffff, 0.08);
        rim.strokeRect(o.x + 5.5, o.y + 5.5, o.w - 11, o.h - 11);
      }
      this.objs.push(rim);
    }
  }

  // -------------------------------------------------------------------------
  // Decorative landmarks — clustered around relays / mid lane
  // -------------------------------------------------------------------------
  private paintLandmarks(map: MapDef): void {
    if (!this.scene.textures.exists(LANDMARK_KEY)) return;
    const placements = this.computeLandmarks(map);
    for (const p of placements) {
      const img = this.scene.add.image(p.x, p.y, LANDMARK_KEY, p.frame);
      img.setOrigin(0.5, 0.5);
      img.setDepth(p.depth);
      img.setScale(p.scale);
      img.setRotation(p.rotation);
      this.objs.push(img);
      if (p.frame === 'beacon') this.beacons.push(img);
    }
  }

  private computeLandmarks(map: MapDef): LandmarkPlacement[] {
    const out: LandmarkPlacement[] = [];
    // Deterministic PRNG so each map looks the same on every reload.
    let seed = (map.id.length * 9301 + 49297) & 0xffffffff;
    const rand = (): number => {
      seed = (seed * 1664525 + 1013904223) >>> 0;
      return seed / 0x100000000;
    };

    const safe = (x: number, y: number, r: number): boolean => {
      if (x < 60 || y < 60 || x > map.width - 60 || y > map.height - 60) return false;
      for (const c of map.cores) {
        if (Math.hypot(c.pos.x - x, c.pos.y - y) < CORE_SAFE_RADIUS) return false;
      }
      for (const n of map.controlNodes) {
        if (Math.hypot(n.pos.x - x, n.pos.y - y) < RELAY_SAFE_RADIUS) return false;
      }
      for (const s of map.spawns) {
        if (Math.hypot(s.pos.x - x, s.pos.y - y) < SPAWN_SAFE_RADIUS) return false;
      }
      for (const o of map.obstacles) {
        // Inflate obstacle rect by r so landmark sprites don't visually clip.
        if (x > o.x - r && x < o.x + o.w + r && y > o.y - r && y < o.y + o.h + r) return false;
      }
      for (const p of out) {
        if (Math.hypot(p.x - x, p.y - y) < LANDMARK_MIN_DIST) return false;
      }
      return true;
    };

    // V1.8 — decorative pylons REMOVED. The original intent was "the
    // relay's antenna array", but the drawPylon() shape (hex plate + 4
    // cardinal antenna spires + cyan emitter disc) is the exact same
    // visual vocabulary as the actual Relays. CrazyGames playtester
    // reported the Relays felt "anonymous" because two of these decorative
    // mini-relays flanked every real one — the player couldn't tell which
    // hex shapes were capture targets. Better to have NO antenna props
    // near the Relays than confusable ones; the Relay sprite + its
    // capture ring carry enough visual weight on their own.
    //
    // The drawPylon() function and the 'pylon' atlas frame are kept so
    // future map decorations (away from Relays) can still use them, but
    // nothing places them right now.
    // 2) Crater dressing around the mid relay / neutral camp.
    const mid = map.neutralCampAnchor ?? map.controlNodes[Math.floor(map.controlNodes.length / 2)]?.pos;
    if (mid !== undefined) {
      for (let i = 0; i < 6; i++) {
        const a = (i * Math.PI) / 3 + rand() * 0.4;
        const rr = 220 + rand() * 60;
        const x = mid.x + Math.cos(a) * rr;
        const y = mid.y + Math.sin(a) * rr * 0.85;
        if (safe(x, y, 30)) {
          out.push({ x, y, frame: 'crater', scale: 0.9 + rand() * 0.4, rotation: rand() * Math.PI, depth: -700 });
        }
      }
    }
    // 3) Random debris piles + beacons scattered over the map.
    // Beacons snap to the underlying hex grid so they read as anchored
    // tactical fixtures, not random props. The bg tile uses HR=32 with
    // pointy-top hex layout anchored at world (0,0). Debris stays free-form
    // (irregular by nature; snapping would make the piles look stamped) and
    // is kept clear of obstacles by the safe() check — wreckage does NOT
    // get placed on top of crates.
    const HR = 32;
    const HW = HR * Math.sqrt(3);
    const ROW_H = HR * 1.5;
    const snapToHex = (x: number, y: number): { x: number; y: number } => {
      // Try the 4 nearest hex centres (two adjacent rows × two adjacent cols)
      // and pick the closest. Odd rows are offset by HW/2 (pointy-top layout).
      const rowF = y / ROW_H;
      let best = { x, y, d: Infinity };
      for (const row of [Math.floor(rowF), Math.ceil(rowF)]) {
        const offX = (row & 1) === 0 ? 0 : HW / 2;
        const colF = (x - offX) / HW;
        for (const col of [Math.floor(colF), Math.ceil(colF)]) {
          const cx = col * HW + offX;
          const cy = row * ROW_H;
          const d = (cx - x) * (cx - x) + (cy - y) * (cy - y);
          if (d < best.d) best = { x: cx, y: cy, d };
        }
      }
      return { x: best.x, y: best.y };
    };

    let attempts = 0;
    // Debris frequency lowered (14 → 8) — they were too dense at the
    // previous count and competed with cores/relays for attention.
    const targetDebris = 8, targetBeacons = 8;
    let placedD = 0, placedB = 0;
    while (attempts++ < 600 && (placedD < targetDebris || placedB < targetBeacons)) {
      const rx = 80 + rand() * (map.width - 160);
      const ry = 80 + rand() * (map.height - 160);
      const wantDebris = placedD < targetDebris && rand() > 0.4;
      const frame = wantDebris ? 'debris' : 'beacon';
      // Beacons snap to hex centres; debris stays where it falls.
      const pos = wantDebris ? { x: rx, y: ry } : snapToHex(rx, ry);
      // safe(_, _, 30) inflates each obstacle rect by 30 px so debris is
      // never placed on top of (or even right next to) a crate.
      if (safe(pos.x, pos.y, 30)) {
        out.push({
          x: pos.x, y: pos.y, frame,
          // V1.8.3 — beacon scale bumped to 0.65..0.85 alongside the
          // signal-marker silhouette (was 0.45..0.65 + pure dot, which read
          // as a coin pickup). Debris stays at 0.55..0.80.
          scale: wantDebris
            ? 0.55 + rand() * 0.25
            : 0.65 + rand() * 0.20,
          // Beacons stay at rotation 0 so they tile cleanly on the grid;
          // debris is naturally irregular and benefits from random rotation.
          rotation: wantDebris ? rand() * Math.PI * 2 : 0,
          depth: frame === 'beacon' ? -440 : -460,
        });
        if (wantDebris) placedD++; else placedB++;
      }
    }
    return out;
  }

  // -------------------------------------------------------------------------
  // World border — neon frame + bracket corner accents
  // -------------------------------------------------------------------------
  private paintBorder(map: MapDef): void {
    const g = this.scene.add.graphics();
    g.setDepth(-300);
    // Glowy outer rim (two passes for a soft halo).
    g.lineStyle(6, BORDER_GLOW, 0.18);
    g.strokeRect(-3, -3, map.width + 6, map.height + 6);
    g.lineStyle(2, BORDER_COLOR, 1);
    g.strokeRect(0, 0, map.width, map.height);
    // Bracket corners — tactical HUD feel.
    g.lineStyle(3, 0x6ad8ff, 0.75);
    const L = 60;
    const corners: Array<[number, number, number, number, number, number]> = [
      [0, 0, L, 0, 0, L],
      [map.width, 0, map.width - L, 0, map.width, L],
      [0, map.height, L, map.height, 0, map.height - L],
      [map.width, map.height, map.width - L, map.height, map.width, map.height - L],
    ];
    for (const [x0, y0, x1, y1, x2, y2] of corners) {
      g.beginPath();
      g.moveTo(x1, y1); g.lineTo(x0, y0); g.lineTo(x2, y2);
      g.strokePath();
    }
    this.objs.push(g);
  }

  // -------------------------------------------------------------------------
  // Vignette + drifting scanlines — fullscreen overlay
  // -------------------------------------------------------------------------
  private paintVignetteAndScanlines(map: MapDef): void {
    if (this.scene.textures.exists(VIGNETTE_KEY)) {
      const v = this.scene.add.image(map.width / 2, map.height / 2, VIGNETTE_KEY);
      v.setOrigin(0.5, 0.5);
      v.setDisplaySize(map.width, map.height);
      v.setDepth(-800);
      v.setAlpha(0.85);
      this.objs.push(v);
    }
    if (this.scene.textures.exists(SCANLINE_KEY)) {
      const s = this.scene.add.tileSprite(0, 0, map.width, map.height, SCANLINE_KEY);
      s.setOrigin(0, 0);
      s.setDepth(-280);
      // V1.8 facelift — dropped 0.35 → 0.18. The CRT line texture was
      // muting saturation across the whole frame; halving the alpha keeps
      // the "tactical HUD" feel without flattening team-color reads.
      s.setAlpha(0.18);
      this.scanlines = s;
      this.objs.push(s);
    }
  }

  // -------------------------------------------------------------------------
  // Ambient atmospheric particles — slow-drifting dust motes + embers
  // -------------------------------------------------------------------------
  private paintAmbient(map: MapDef): void {
    if (!this.scene.textures.exists(FX_KEY)) return;
    // Deterministic PRNG so each map's atmosphere looks the same on reload
    // (keeps the visual "feel" of a map stable rather than randomly different).
    let seed = ((map.id.length * 2654435761) ^ 0xdeadbeef) >>> 0;
    const rand = (): number => {
      seed = (seed * 1664525 + 1013904223) >>> 0;
      return seed / 0x100000000;
    };
    const make = (frame: string, count: number, tint: number, sizeMin: number, sizeMax: number, speed: number, depth: number, baseAlpha: number, pulseHz: number): void => {
      for (let i = 0; i < count; i++) {
        const img = this.scene.add.image(rand() * map.width, rand() * map.height, FX_KEY, frame);
        img.setOrigin(0.5, 0.5);
        img.setDepth(depth);
        img.setBlendMode(Phaser.BlendModes.ADD);
        const size = sizeMin + rand() * (sizeMax - sizeMin);
        img.setDisplaySize(size, size);
        img.setTint(tint);
        img.setAlpha(baseAlpha);
        // Random drift direction at the per-particle base speed.
        const a = rand() * Math.PI * 2;
        const sp = speed * (0.5 + rand() * 1.0);
        this.ambient.push({
          img,
          vx: Math.cos(a) * sp,
          vy: Math.sin(a) * sp,
          phaseMs: rand() * 4000,
          pulseHz: pulseHz * (0.7 + rand() * 0.6),
          baseAlpha,
          worldW: map.width,
          worldH: map.height,
        });
        this.objs.push(img);
      }
    };
    // V1.8 facelift — dust alpha 0.18 → 0.30; embers alpha 0.28 → 0.55 and
    // size range expanded ~1.4×. Embers now read clearly at 0.5× zoom on
    // mid-range mobile; dust adds visible motion in the lit zones.
    // Dust: tiny, dim, sky-blue/grey, drifts faster.
    make('dust_mote', AMBIENT_DUST_COUNT, 0xc0d0e0, 7, 16, 16, -260, 0.30, 0.35);
    // Embers: bright warm orange, drift slower, pulse stronger.
    make('ember', AMBIENT_EMBER_COUNT, 0xffa060, 10, 22, 10, -250, 0.55, 0.55);
  }
}
